import styled from '@emotion/styled'
import { QuestionCircleOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { MouseEvent, ReactNode } from 'react'
import Text from 'src/components/ts-components/Text'

// Canlan keeps card text white on the selected (primary-coloured) card; SportNinja
// uses dark text for legibility on yellow.
const SELECTED_TEXT_PRIMARY = isCanlan ? colors.WHITE : colors.SECONDARY_300
const SELECTED_TEXT_SECONDARY = isCanlan ? colors.WHITE : colors.NEUTRAL_600

type Props = {
  icon: ReactNode
  title: string
  description?: string
  selected: boolean
  recommended?: boolean
  /** Renders a "Learn more" link at the bottom of the card. */
  learnMoreUrl?: string
  onClick: () => void
  testId?: string
  disabled?: boolean
}

const ChoiceCard = ({
  icon,
  title,
  description,
  selected,
  recommended,
  learnMoreUrl,
  onClick,
  testId,
  disabled,
}: Props) => {
  const handleLearnMore = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    if (learnMoreUrl) {
      window.open(learnMoreUrl, '_blank')
    }
  }

  return (
    <Card
      type='button'
      onClick={onClick}
      data-testid={testId}
      $selected={selected}
      disabled={disabled}
    >
      <IconCircle $selected={selected}>{icon}</IconCircle>
      <Text
        variant='heading5'
        weight='semiBold'
        color={selected ? SELECTED_TEXT_PRIMARY : colors.WHITE}
      >
        {title}
      </Text>
      {description ? (
        <Text
          variant='paragraphSmall'
          color={selected ? SELECTED_TEXT_SECONDARY : colors.NEUTRAL_50}
        >
          {description}
        </Text>
      ) : null}
      {recommended ? (
        <Text variant='paragraphXSmall' color={colors.INFO_300}>
          Most Common
        </Text>
      ) : null}
      {learnMoreUrl ? (
        <LearnMore type='button' onClick={handleLearnMore}>
          <Text
            variant='paragraphXSmall'
            color={selected ? SELECTED_TEXT_PRIMARY : colors.PRIMARY}
          >
            Learn more
          </Text>
          <QuestionCircleOutlined
            style={{
              fontSize: 16,
              color: selected ? SELECTED_TEXT_PRIMARY : colors.PRIMARY,
            }}
          />
        </LearnMore>
      ) : null}
    </Card>
  )
}

export default ChoiceCard

// ---- styled ----
const Card = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 40px 18px;
  height: 246px;
  border-radius: 16px;
  border: 1px solid
    ${(p) => (p.$selected ? colors.PRIMARY_DARK : colors.SECONDARY_300)};
  background: ${(p) => (p.$selected ? colors.PRIMARY : colors.SECONDARY_400)};
  cursor: pointer;
  transition: border-color 120ms ease, background 120ms ease;
  text-align: center;
  &:hover {
    border-color: ${colors.PRIMARY};
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
  &:disabled:hover {
    border-color: ${colors.SECONDARY_300};
  }
`

const LearnMore = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
  &,
  & * {
    cursor: pointer;
  }
`

const IconCircle = styled.div<{ $selected: boolean }>`
  width: 72px;
  height: 72px;
  flex-shrink: 0;
  padding: 16px;
  border-radius: 50%;
  background: ${colors.SECONDARY_300};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => (p.$selected ? colors.PRIMARY : colors.PRIMARY)};
`
